import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { 
  Activity, 
  DollarSign, 
  TrendingUp, 
  Download,
  Calendar,
  Users,
  Zap,
  AlertCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface UsageLog {
  id: string;
  user_id: string;
  function_name: string;
  tokens_used: number;
  cost: number;
  created_at: string;
}

interface AnalyticsData {
  totalUsage: number;
  totalCost: number;
  totalTokens: number;
  averageTokensPerRequest: number;
  mostUsedFeature: string;
  usageByFeature: Array<{ name: string; value: number; cost: number }>;
  dailyUsage: Array<{ date: string; requests: number; tokens: number; cost: number }>;
  userActivity: Array<{ user_id: string; requests: number; tokens: number }>;
}

const FEATURE_COLORS = {
  'mcq-generation': '#8b5cf6',
  'explanation-generation': '#06b6d4',
  'note-assistance': '#10b981',
  'template-generation': '#f59e0b',
  'content-suggestions': '#ef4444',
} as const;

export default function AIAnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const days = parseInt(timeRange);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('ai_usage_logs')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      setUsageLogs(data || []);
      calculateAnalytics(data || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (logs: UsageLog[]) => {
    const totalUsage = logs.length;
    const totalCost = logs.reduce((sum, log) => sum + (log.cost || 0), 0);
    const totalTokens = logs.reduce((sum, log) => sum + log.tokens_used, 0);
    const averageTokensPerRequest = totalUsage > 0 ? totalTokens / totalUsage : 0;

    // Feature usage analysis
    const featureUsage = logs.reduce((acc, log) => {
      const feature = log.function_name;
      if (!acc[feature]) {
        acc[feature] = { count: 0, cost: 0, tokens: 0 };
      }
      acc[feature].count++;
      acc[feature].cost += log.cost || 0;
      acc[feature].tokens += log.tokens_used;
      return acc;
    }, {} as Record<string, { count: number; cost: number; tokens: number }>);

    const usageByFeature = Object.entries(featureUsage).map(([name, data]) => ({
      name: name.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: data.count,
      cost: data.cost,
      tokens: data.tokens
    }));

    const mostUsedFeature = usageByFeature.reduce((max, feature) => 
      feature.value > max.value ? feature : max, 
      { name: 'None', value: 0, cost: 0, tokens: 0 }
    ).name;

    // Daily usage trends
    const dailyData = logs.reduce((acc, log) => {
      const date = new Date(log.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = { requests: 0, tokens: 0, cost: 0 };
      }
      acc[date].requests++;
      acc[date].tokens += log.tokens_used;
      acc[date].cost += log.cost || 0;
      return acc;
    }, {} as Record<string, { requests: number; tokens: number; cost: number }>);

    const dailyUsage = Object.entries(dailyData)
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // User activity
    const userData = logs.reduce((acc, log) => {
      const userId = log.user_id;
      if (!acc[userId]) {
        acc[userId] = { requests: 0, tokens: 0 };
      }
      acc[userId].requests++;
      acc[userId].tokens += log.tokens_used;
      return acc;
    }, {} as Record<string, { requests: number; tokens: number }>);

    const userActivity = Object.entries(userData)
      .map(([user_id, data]) => ({ user_id, ...data }))
      .sort((a, b) => b.requests - a.requests)
      .slice(0, 10);

    setAnalytics({
      totalUsage,
      totalCost,
      totalTokens,
      averageTokensPerRequest,
      mostUsedFeature,
      usageByFeature,
      dailyUsage,
      userActivity
    });
  };

  const exportData = () => {
    const csvContent = [
      ['Date', 'Feature Type', 'Tokens Used', 'Cost (USD)', 'User ID'],
      ...usageLogs.map(log => [
        new Date(log.created_at).toLocaleDateString(),
        log.function_name,
        log.tokens_used.toString(),
        (log.cost || 0).toFixed(4),
        log.user_id
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-usage-analytics-${timeRange}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: "Analytics data exported successfully",
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <Skeleton className="h-4 w-20" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Select value={timeRange} onValueChange={(value: '7d' | '30d' | '90d') => setTimeRange(value)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadAnalytics} variant="outline" size="sm">
            <Activity className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <Button onClick={exportData} variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Activity className="h-4 w-4 text-primary" />
              Total Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalUsage || 0}</div>
            <p className="text-xs text-muted-foreground">
              AI API calls made
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Zap className="h-4 w-4 text-warning" />
              Total Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalTokens.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">
              Avg: {Math.round(analytics?.averageTokensPerRequest || 0)} per request
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <DollarSign className="h-4 w-4 text-success" />
              Total Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${analytics?.totalCost.toFixed(2) || '0.00'}</div>
            <p className="text-xs text-muted-foreground">
              AI API usage cost
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <TrendingUp className="h-4 w-4 text-primary" />
              Top Feature
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">{analytics?.mostUsedFeature || 'None'}</div>
            <p className="text-xs text-muted-foreground">
              Most used AI feature
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Usage Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Daily Usage Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics?.dailyUsage || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value, name) => [value, name === 'requests' ? 'Requests' : name === 'tokens' ? 'Tokens' : 'Cost ($)']}
                />
                <Line type="monotone" dataKey="requests" stroke="hsl(var(--primary))" strokeWidth={2} />
                <Line type="monotone" dataKey="tokens" stroke="hsl(var(--warning))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Feature Usage Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Feature Usage Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics?.usageByFeature || []}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {analytics?.usageByFeature.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(FEATURE_COLORS)[index % Object.values(FEATURE_COLORS).length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, 'Requests']} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Feature Cost Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Cost by Feature
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics?.usageByFeature || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => [`$${Number(value).toFixed(4)}`, 'Cost']} />
                <Bar dataKey="cost" fill="hsl(var(--success))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Top Users by Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.userActivity.slice(0, 5).map((user, index) => (
                <div key={user.user_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="w-6 h-6 rounded-full p-0 flex items-center justify-center text-xs">
                      {index + 1}
                    </Badge>
                    <span className="text-sm font-mono">{user.user_id.slice(0, 8)}...</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{user.requests} requests</div>
                    <div className="text-xs text-muted-foreground">{user.tokens.toLocaleString()} tokens</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}